import { computed, observable } from 'mobx';

import ApiService from '@app/Services/ApiService';
import { appStore } from '@app/Stores/AppStore';
import { ApiUrls, PrincipalConfig } from '@app/AppConstants';
import { PermissionModel, PredefinedRoleModel } from '@app/Models/Models';
import { UserPermissions } from '@app/Components/AzureAuth';
import { configurationStore } from './ConfigurationStore';

type MatchedRole = PredefinedRoleModel & {
    matches: number;
    kpi: number;
};

class PermissionStore {
    @observable roles: PredefinedRoleModel[] = [];
    @observable permissions: PermissionModel[] = [];

    @observable permissionsEnabled: boolean;
    @observable permissionsFlagLoaded: boolean;
    @observable permissionsLoaded: boolean = false;

    @observable manualFlightsEnabled: boolean = false;
    @observable flightsNeedingAttentionEnabled: boolean = false;

    public init() {
        void this.loadFeatureFlag();
        void this.loadManualFlightsFlag();
        void this.loadFlightsNeedingAttentionFlag();
        void this.loadPermissions();
    }

    private async loadFeatureFlag() {
        const data = await configurationStore.get(PrincipalConfig.feature, PrincipalConfig.EnablePermissionsCheck);
        this.permissionsEnabled = data?.toLowerCase() === 'true';
        this.permissionsFlagLoaded = true;
    }

    private async loadManualFlightsFlag() {
        const data = await configurationStore.get(PrincipalConfig.feature, PrincipalConfig.EnableManualFlights);
        this.manualFlightsEnabled = data?.toLowerCase() === 'true';
    }

    private async loadFlightsNeedingAttentionFlag() {
        const data = await configurationStore.get(PrincipalConfig.feature, PrincipalConfig.EnableFlightsNeedingAttention);
        this.flightsNeedingAttentionEnabled = data?.toLowerCase() === 'true';
    }

    @computed
    public get realUserPermissions(): number[] {
        const user = appStore.currentUser;
        const permissionIds = this.getPermissionIdsFromEncodedRoles(user?.roles || []) || [];
        return this.expandWithParentPermissions(permissionIds);
    }

    @computed
    public get isImpersonatingPermissions(): boolean {
        return !!appStore.permissionImpersonation;
    }

    @computed
    public get currentUserPermissions(): number[] {
        const impersonation = appStore.permissionImpersonation;
        if (!impersonation) {
            return this.realUserPermissions;
        }

        return this.expandWithParentPermissions(impersonation.permissionIds);
    }

    public expandWithParentPermissions(permissionIds: number[]): number[] {
        const parentById = this.parentPermissionById;
        if (!parentById.size) return permissionIds;

        const granted = new Set(permissionIds);
        for (const permission of this.permissions) {
            if (granted.has(permission.id)) continue;

            const visited = new Set<number>();
            let ancestorId = parentById.get(permission.id);
            while (ancestorId !== undefined && !visited.has(ancestorId)) {
                visited.add(ancestorId);
                if (granted.has(ancestorId)) {
                    granted.add(permission.id);
                    break;
                }
                ancestorId = parentById.get(ancestorId);
            }
        }

        return Array.from(granted);
    }

    @computed
    private get parentPermissionById(): Map<number, number> {
        const map = new Map<number, number>();
        for (const permission of this.permissions) {
            if (permission.parentPermissionId) {
                map.set(permission.id, permission.parentPermissionId);
            }
        }
        return map;
    }

    public async loadPermissions() {
        try {
            const { data } = await ApiService.get<PermissionModel[]>(ApiUrls.PermissionUrl);
            this.permissions = data;
        } finally {
            this.permissionsLoaded = true;
        }
    }

    public async loadRoles() {
        const { data } = await ApiService.get<PredefinedRoleModel[]>(ApiUrls.PermissionRoleUrl);
        this.roles = data;
    }

    public encodeBytesToBase64(bytes: number[]) {
        return bytes.length ? '1' + btoa(String.fromCharCode.apply(null, bytes)) : '';
    }

    public decodeBase64ToBytes(encodedStr: string) {
        if (!encodedStr.startsWith('1')) return;

        const base64regex = /^([0-9a-zA-Z+/]{4})*(([0-9a-zA-Z+/]{2}==)|([0-9a-zA-Z+/]{3}=))?$/;
        const base64Str = encodedStr.slice(1);
        const isValid = base64regex.test(base64Str);

        if (isValid) {
            return atob(base64Str).split('').map(c => c.charCodeAt(0));
        }
    }

    public convertPermissionIdsToBytes(ids?: number[]) {
        if (!ids || !ids.length) return [];

        const maxId = Math.max(...ids);
        const bytesCount = Math.ceil(maxId / 8);
        const bytes = new Uint8Array(bytesCount);
        ids.forEach(id => {
            const byteIndex = Math.floor((id - 1) / 8);
            const bitIndex = ((id - 1) - byteIndex * 8);
            bytes[byteIndex] = bytes[byteIndex] | (128 >>> bitIndex);
        });

        return Array.from(bytes);
    }

    public convertBytesToPermissionIds(bytes?: number[]) {
        if (!bytes) return [];

        const ids: number[] = [];
        const bitesCount = bytes.length * 8;
        for (let i = 0; i < bitesCount; i++) {
            const byteIndex = Math.floor(i / 8);
            const bitIndex = (i - byteIndex * 8);
            const isBitSet = (bytes[byteIndex] & (128 >>> bitIndex)) !== 0;
            if (isBitSet) {
                const permissionId = i + 1;
                ids.push(permissionId);
            }
        }

        return ids;
    }

    public getPermissionIdsFromEncodedRoles(encodedStrings: string[]) {
        if (!encodedStrings.length) return;

        let permissionIds: number[] = [];
        for (let str of encodedStrings) {
            const bytes = this.decodeBase64ToBytes(str);
            const ids = this.convertBytesToPermissionIds(bytes);
            permissionIds = permissionIds.concat(ids);
        }

        return permissionIds;
    }

    public getRolesMatchFromEncodedRoles(encodedRoles: string[]) {
        const permissionIds = this.getPermissionIdsFromEncodedRoles(encodedRoles);
        const rolesMatch = this.getRolesMatch(permissionIds)?.text;

        return rolesMatch || '';
    }

    private _getMostProbableRoles(selectedPermissions: number[]) {
        const probableRoles: MatchedRole[] = [];
        this.roles.forEach(role => {
            if (!role.permissionIds) return;
            const { permissionIds } = role;

            const minKpi = 50;
            const matchesCount = permissionIds.filter(p => selectedPermissions.includes(p)).length;
            let kpi = 0;
            if (!!matchesCount && !!permissionIds.length) {
                kpi = (matchesCount / permissionIds.length) * 100;
            }
            if (matchesCount > 0 && kpi >= minKpi) {
                probableRoles.push({ matches: matchesCount, kpi, ...role });
            }
        });

        const mostProbableRoles = probableRoles.sort((a, b) => b.matches - a.matches || b.kpi - a.kpi);
        return mostProbableRoles;
    }

    public getRolesMatch(selectedPermissions?: number[]) {
        if (!selectedPermissions || !selectedPermissions.length || !this.roles.length) return null;

        const mostProbableRoles: MatchedRole[] = this._getMostProbableRoles(selectedPermissions);
        let matchedRoles: MatchedRole[] = [];
        let remaining = [...selectedPermissions];
        const missing: { id: number, codeName: string, permissionIds: number[] }[] = [];
        const missingSet = new Set<number>();

        for (const mostProbableRole of mostProbableRoles) {
            const rolePermissions = mostProbableRole.permissionIds.filter(p => remaining.includes(p));
            if (!rolePermissions.length) continue;

            const missingRolePermissions = mostProbableRole.permissionIds.filter(p => !selectedPermissions.includes(p));
            const isFullMatch = missingRolePermissions.length === 0;
            const isPartialMatch = missingRolePermissions.length > 0 && missingRolePermissions.length <= 3;

            if (isFullMatch || isPartialMatch) {
                matchedRoles.push(mostProbableRole);
                remaining = remaining.filter(p => !mostProbableRole.permissionIds.includes(p));
            }

            if (isPartialMatch && !isFullMatch) {
                missing.push({ id: mostProbableRole.id, codeName: mostProbableRole.codeName, permissionIds: missingRolePermissions });
                missingRolePermissions.forEach(p => missingSet.add(p));
            }

            if ((isFullMatch && remaining.length === 0) || (isPartialMatch && remaining.length <= 3 && missingSet.size <= 3)) {
                break;
            }
        }

        if (matchedRoles.length) {
            let result: string = matchedRoles.map(r => `${r.codeName} role`).join(' + ');
            let resultSql: string = '';

            if (remaining.length > 3 || missingSet.size > 3) {
                result = 'Custom role';
            } else {
                if (remaining.length) {
                    result += ` + ${remaining.map(p => UserPermissions[p]).join(', ')}`;
                }
                if (missingSet.size) {
                    result += ` excluding ${Array.from(missingSet).map(p => UserPermissions[p]).join(', ')}`;
                }
            }

            const mostMatchedRole = matchedRoles[0];
            const extraPermissionsSql = selectedPermissions
                .filter(p => !mostMatchedRole.permissionIds.includes(p))
                .map(p => `(${mostMatchedRole.id} /* ${mostMatchedRole.codeName} */, ${p} /* ${UserPermissions[p]} */)`);
            if (extraPermissionsSql.length) {
                resultSql += `/* ${mostMatchedRole.codeName} changes */\n`;
                resultSql += `INSERT INTO [sec].[PredefinedRolePermission] ([RoleId], [PermissionId]) \nVALUES \n${extraPermissionsSql.join(',\n')}\nGO\n`;
            }

            const missingPermissionsSql = mostMatchedRole.permissionIds.filter(p => !selectedPermissions.includes(p));
            if (missingPermissionsSql.length) {
                resultSql += `/* ${mostMatchedRole.codeName} changes */\n`;
                missingPermissionsSql.forEach(p => {
                    resultSql += `DELETE FROM [sec].[PredefinedRolePermission] WHERE [RoleId] = ${mostMatchedRole.id} /* ${mostMatchedRole.codeName} */ AND [PermissionId] = ${p} /* ${UserPermissions[p]} */\nGO\n`;
                });
            }

            return {
                text: result,
                sql: resultSql
            };
        }

        return {
            text: selectedPermissions.length ? 'Custom role' : '',
            sql: null
        };
    }
}

export const permissionStore = new PermissionStore();
